import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { getAllOrders, searchOrders as searchOrdersService } from './orderService';
import { getAllClients } from './clientService';
import { getAllTechnicians } from './technicianService';
import { getAllEquipment } from './equipmentService';
import { getAllBrands } from './brandService';
import { getBulkClientBlacklistStatusFromCache } from './blacklistService';
import { normalizeSearchText, matchesSearchTerm } from './searchUtils';

/**
 * Global Search Service for STB Workshop
 * Provides comprehensive search across all system data with smart detection
 */

// Search result types
export const SEARCH_TYPES = {
  ORDER: 'order',
  CLIENT: 'client',
  TECHNICIAN: 'technician',
  EQUIPMENT: 'equipment'
};

// Pattern matchers
const PHONE_PATTERN = /^[\d\s\-\(\)\+]{2,}$/;  // Allow shorter searches, common phone formats
const FOLIO_PATTERN = /^\d+$/;

/**
 * Detects search intent based on input pattern
 */
export const detectSearchType = (searchTerm) => {
  const cleanTerm = searchTerm.trim();
  const cleanPhoneTest = cleanTerm.replace(/\s/g, '');
  
  // Check for folio patterns first (pure numbers 1-5 digits are likely folios)
  if (FOLIO_PATTERN.test(cleanTerm) && cleanTerm.length <= 5) {
    return 'folio';
  }
  
  // Check for phone patterns (6+ digits or numbers with formatting)
  if (PHONE_PATTERN.test(cleanPhoneTest) && cleanPhoneTest.length >= 2) {
    // If it contains formatting characters, it's definitely a phone
    if (cleanTerm !== cleanPhoneTest) {
      return 'phone';
    }
    
    // For pure numbers, treat 6+ digits as phone numbers
    if (cleanPhoneTest.length >= 6) {
      return 'phone';
    }
  }
  
  return 'text';
};

/**
 * Gets the principal contact from contactos array
 */
const getPrincipalContact = (contactos) => {
  if (!contactos || contactos.length === 0) return null;
  return contactos.find(c => c.esPrincipal) || contactos[0];
};

/**
 * Gets the display name for a client (matches ClientsListTab logic)
 */
const getClientDisplayName = (client) => {
  const principal = getPrincipalContact(client.contactos);
  return client.nombreEmpresa || principal?.nombre || 'Cliente';
};

/**
 * Builds searchable text from client data
 */
const buildClientSearchText = (client) => {
  const parts = [];
  
  // Basic info using correct field names
  if (client.nombreEmpresa) parts.push(client.nombreEmpresa);
  
  // Legacy fields for backward compatibility
  if (client.nombre) parts.push(client.nombre);
  if (client.companyName) parts.push(client.companyName);
  if (client.contactName) parts.push(client.contactName);
  
  // Contact info (using correct field name: contactos)
  if (client.contactos) {
    client.contactos.forEach(contact => {
      if (contact.nombre) parts.push(contact.nombre);
      if (contact.telefono) parts.push(contact.telefono);
      if (contact.email) parts.push(contact.email);
      if (contact.nota) parts.push(contact.nota);
    });
  }
  
  // Legacy contacts field (for backward compatibility)
  if (client.contacts) {
    client.contacts.forEach(contact => {
      if (contact.nombre) parts.push(contact.nombre);
      if (contact.telefono) parts.push(contact.telefono);
      if (contact.email) parts.push(contact.email);
    });
  }
  
  // Address
  if (client.direccion) parts.push(client.direccion);
  if (client.ciudad) parts.push(client.ciudad);
  if (client.estado) parts.push(client.estado);
  
  // Notes
  if (client.notas) parts.push(client.notas);
  
  return normalizeSearchText(parts.join(' '));
};

/**
 * Finds which specific field matches the search term and returns match info
 */
const findOrderFieldMatch = (order, searchTerm, brands = []) => {
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  
  // Define all searchable fields with their display names
  const searchableFields = [
    // Basic info
    { field: 'folio', name: 'Folio', value: order.folio?.toString() },
    { field: 'tipo_de_orden', name: 'Tipo de orden', value: order.tipo_de_orden },
    { field: 'status_orden', name: 'Estado', value: order.status_orden },
    
    // Client info  
    { field: 'clienteName', name: 'Cliente', value: order.clienteName },
    { field: 'clienteCompanyName', name: 'Empresa', value: order.clienteCompanyName },
    { field: 'clienteContactName', name: 'Contacto', value: order.clienteContactName },
    
    // Technician
    { field: 'technicianName', name: 'Técnico', value: order.technicianName },
    
    // Text content fields
    { field: 'observaciones_detalle', name: 'Observaciones', value: order.observaciones_detalle },
    { field: 'comentarios_cliente', name: 'Comentarios del cliente', value: order.comentarios_cliente },
    { field: 'resultados_revision', name: 'Resultados de revisión', value: order.resultados_revision },
    { field: 'detalle_veredicto', name: 'Detalle del veredicto', value: order.detalle_veredicto },
    { field: 'veredicto', name: 'Veredicto', value: order.veredicto },
    { field: 'quien_recibe_equipo', name: 'Quien recibe equipo', value: order.quien_recibe_equipo },
    
    // Reference numbers
    { field: 'folio_cotizacion', name: 'Folio de cotización', value: order.folio_cotizacion },
    { field: 'folio_orden_compra', name: 'Folio orden de compra', value: order.folio_orden_compra },
    { field: 'folio_factura_contpaq', name: 'Folio factura', value: order.folio_factura_contpaq },
    { field: 'folio_salida_contpaq', name: 'Folio salida', value: order.folio_salida_contpaq },
    { field: 'folio_compra', name: 'Folio de compra', value: order.folio_compra },
    { field: 'rma', name: 'RMA', value: order.rma },
    
    // Status and financial
    { field: 'estatus_pago', name: 'Estado de pago', value: order.estatus_pago },
    { field: 'importe', name: 'Importe', value: order.importe?.toString() }
  ];
  
  // Check equipment fields
  if (order.equipos) {
    order.equipos.forEach((equipo, index) => {
      if (equipo.descripcion) {
        searchableFields.push({
          field: `equipo_${index}_descripcion`,
          name: 'Equipo',
          value: equipo.descripcion
        });
      }
      if (equipo.series) {
        searchableFields.push({
          field: `equipo_${index}_series`,
          name: 'Serie de equipo',
          value: equipo.series
        });
      }
      if (equipo.catalogData) {
        const cat = equipo.catalogData;
        if (cat.tipo) searchableFields.push({ field: `equipo_${index}_tipo`, name: 'Tipo de equipo', value: cat.tipo });
        
        // Handle brand with dynamic lookup from ID (preferred) or legacy marca field
        if (cat.marcaId) {
          const brand = brands.find(b => b.id === cat.marcaId);
          if (brand && brand.nombre) {
            searchableFields.push({ field: `equipo_${index}_marca`, name: 'Marca', value: brand.nombre });
          }
        } else if (cat.marca) {
          // Legacy support for old orders with marca field
          searchableFields.push({ field: `equipo_${index}_marca`, name: 'Marca', value: cat.marca });
        }
        
        if (cat.modelo) searchableFields.push({ field: `equipo_${index}_modelo`, name: 'Modelo', value: cat.modelo });
        if (cat.numeroParte) searchableFields.push({ field: `equipo_${index}_parte`, name: 'Número de parte', value: cat.numeroParte });
        
        // Include specifications
        if (cat.especificaciones) {
          Object.entries(cat.especificaciones).forEach(([specName, specValue]) => {
            if (specValue) {
              searchableFields.push({ 
                field: `equipo_${index}_spec_${specName}`, 
                name: `Especificación: ${specName}`, 
                value: specValue.toString() 
              });
            }
          });
        }
      }
      
      // Include custom specifications from the order
      if (equipo.customSpecs) {
        Object.entries(equipo.customSpecs).forEach(([specName, specValue]) => {
          if (specValue) {
            searchableFields.push({ 
              field: `equipo_${index}_custom_${specName}`, 
              name: `Especificación personalizada: ${specName}`, 
              value: specValue.toString() 
            });
          }
        });
      }
    });
  }
  
  // Find the first field that matches
  for (const fieldInfo of searchableFields) {
    if (fieldInfo.value && matchesSearchTerm(fieldInfo.value, searchTerm)) {
      return {
        matchField: fieldInfo.name,
        matchedText: fieldInfo.value,
        fieldKey: fieldInfo.field
      };
    }
  }
  
  return null;
};

/**
 * Builds searchable text from order data
 */
const buildOrderSearchText = (order, brands = []) => {
  const parts = [];
  
  // Basic info
  if (order.folio) parts.push(order.folio.toString());
  if (order.tipo_de_orden) parts.push(order.tipo_de_orden);
  if (order.status_orden) parts.push(order.status_orden);
  
  // Client info
  if (order.clienteName) parts.push(order.clienteName);
  if (order.clienteCompanyName) parts.push(order.clienteCompanyName);
  if (order.clienteContactName) parts.push(order.clienteContactName);
  if (order.clientePhone) parts.push(order.clientePhone);
  
  // Equipment info
  if (order.equipos) {
    order.equipos.forEach(equipo => {
      if (equipo.descripcion) parts.push(equipo.descripcion);
      if (equipo.series) parts.push(equipo.series);
      if (equipo.catalogData) {
        const cat = equipo.catalogData;
        if (cat.tipo) parts.push(cat.tipo);
        
        // Handle brand with dynamic lookup from ID (preferred) or legacy marca field
        if (cat.marcaId) {
          const brand = brands.find(b => b.id === cat.marcaId);
          if (brand && brand.nombre) {
            parts.push(brand.nombre);
          }
        } else if (cat.marca) {
          // Legacy support for old orders with marca field
          parts.push(cat.marca);
        }
        
        if (cat.modelo) parts.push(cat.modelo);
        if (cat.numeroParte) parts.push(cat.numeroParte);
        
        // Include specifications
        if (cat.especificaciones) {
          Object.values(cat.especificaciones).forEach(spec => {
            if (spec) parts.push(spec.toString());
          });
        }
      }
      
      // Include custom specifications from the order
      if (equipo.customSpecs) {
        Object.values(equipo.customSpecs).forEach(spec => {
          if (spec) parts.push(spec.toString());
        });
      }
    });
  }
  
  // Technician info
  if (order.technicianName) parts.push(order.technicianName);
  
  // Text content fields
  if (order.observaciones_detalle) parts.push(order.observaciones_detalle);
  if (order.comentarios_cliente) parts.push(order.comentarios_cliente);
  if (order.resultados_revision) parts.push(order.resultados_revision);
  if (order.detalle_veredicto) parts.push(order.detalle_veredicto);
  if (order.veredicto) parts.push(order.veredicto);
  if (order.quien_recibe_equipo) parts.push(order.quien_recibe_equipo);
  
  // Reference numbers and identifiers
  if (order.folio_cotizacion) parts.push(order.folio_cotizacion);
  if (order.folio_orden_compra) parts.push(order.folio_orden_compra);
  if (order.folio_factura_contpaq) parts.push(order.folio_factura_contpaq);
  if (order.folio_salida_contpaq) parts.push(order.folio_salida_contpaq);
  if (order.folio_compra) parts.push(order.folio_compra);
  if (order.rma) parts.push(order.rma);
  
  // Status and monetary fields
  if (order.estatus_pago) parts.push(order.estatus_pago);
  if (order.importe) parts.push(order.importe.toString());
  
  return normalizeSearchText(parts.join(' '));
};

/**
 * Builds searchable text from technician data
 */
const buildTechnicianSearchText = (technician) => {
  const parts = [];
  
  if (technician.nombre) parts.push(technician.nombre);
  if (technician.apellido) parts.push(technician.apellido);
  if (technician.telefono) parts.push(technician.telefono);
  if (technician.email) parts.push(technician.email);
  if (technician.especialidad) parts.push(technician.especialidad);
  if (technician.notas) parts.push(technician.notas);
  
  return normalizeSearchText(parts.join(' '));
};

/**
 * Builds searchable text from equipment data
 */
const buildEquipmentSearchText = (equipment, brands = []) => {
  const parts = [];
  
  if (equipment.tipo) parts.push(equipment.tipo);
  if (equipment.marcaId) {
    // Look up brand name dynamically from ID
    const brand = brands.find(b => b.id === equipment.marcaId);
    if (brand && brand.nombre) {
      parts.push(brand.nombre);
    }
  }
  if (equipment.modelo) parts.push(equipment.modelo);
  if (equipment.numeroParte) parts.push(equipment.numeroParte);
  if (equipment.descripcion) parts.push(equipment.descripcion);
  
  // Include alternate part numbers
  if (equipment.partesAlternativas && Array.isArray(equipment.partesAlternativas)) {
    equipment.partesAlternativas.forEach(altPart => {
      if (altPart && altPart.trim()) {
        parts.push(altPart.trim());
      }
    });
  }
  
  // Include specifications
  if (equipment.especificaciones) {
    Object.values(equipment.especificaciones).forEach(spec => {
      if (spec) parts.push(spec.toString());
    });
  }
  
  return normalizeSearchText(parts.join(' '));
};

/**
 * Highlights matching text in search results (accent-insensitive)
 */
export const highlightMatches = (text, searchTerm) => {
  if (!text || !searchTerm) return text;
  
  // Use a more sophisticated matching that handles accents
  const normalizedText = normalizeSearchText(text);
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  
  if (!normalizedText.includes(normalizedSearchTerm)) return text;
  
  // Create accent-insensitive regex pattern
  // Convert each character in the search term to a pattern that matches accented variants
  const createAccentInsensitivePattern = (term) => {
    const accentMap = {
      'a': '[aáàâäãåā]', 'e': '[eéèêëē]', 'i': '[iíìîïī]', 
      'o': '[oóòôöõōø]', 'u': '[uúùûüū]', 'n': '[nñ]',
      'c': '[cç]', 'y': '[yý]'
    };
    
    return term
      .toLowerCase()
      .split('')
      .map(char => accentMap[char] || char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('');
  };
  
  const accentInsensitivePattern = createAccentInsensitivePattern(searchTerm);
  const searchRegex = new RegExp(accentInsensitivePattern, 'gi');
  
  return text.replace(searchRegex, '<mark class="bg-yellow-200 px-0.5 rounded">$&</mark>');
};

/**
 * Fetches all clients for search index using cached service
 */
const fetchAllClients = async () => {
  try {
    const clients = await getAllClients();
    // Filter out archived clients for search
    return clients.filter(client => !client.isArchived);
  } catch (error) {
    console.error('Error fetching clients for search:', error);
    return [];
  }
};

/**
 * Fetches all technicians for search index using cached service
 */
const fetchAllTechnicians = async () => {
  try {
    const technicians = await getAllTechnicians();
    // Filter out archived technicians for search
    return technicians.filter(tech => !tech.isArchived);
  } catch (error) {
    console.error('Error fetching technicians for search:', error);
    return [];
  }
};

/**
 * Fetches all equipment from catalog for search index using cached service
 */
const fetchAllEquipment = async () => {
  try {
    const equipment = await getAllEquipment();
    // Filter out archived equipment for search
    return equipment.filter(equip => !equip.isArchived);
  } catch (error) {
    console.error('Error fetching equipment for search:', error);
    return [];
  }
};

/**
 * Fetches all brands for search index using cached service
 */
const fetchAllBrands = async () => {
  try {
    const brands = await getAllBrands();
    return brands; // Brands don't have archived status
  } catch (error) {
    console.error('Error fetching brands for search:', error);
    return [];
  }
};

/**
 * Searches across clients
 */
const searchClients = async (searchTerm, searchType) => {
  const clients = await fetchAllClients();
  
  // Get blacklist status for all clients
  const clientIds = clients.map(client => client.id);
  const blacklistStatus = await getBulkClientBlacklistStatusFromCache(clientIds);
  
  const results = [];
  
  clients.forEach(client => {
    let match = false;
    let matchField = '';
    let displayText = '';
    let matchedContact = null;
    
    // UNIVERSAL SEARCH - Always search phone numbers regardless of search type
    const phoneQuery = searchTerm.replace(/\s/g, '');
    
    // Search in contactos for phone matches (partial matches)
    if (client.contactos && phoneQuery.length >= 2 && /^\d+$/.test(phoneQuery)) {
      for (const contact of client.contactos) {
        if (contact.telefono) {
          const clientPhone = contact.telefono.replace(/\s/g, '');
          if (clientPhone.includes(phoneQuery)) {
            match = true;
            matchField = 'Teléfono';
            displayText = contact.telefono;
            matchedContact = contact;
            break;
          }
        }
      }
    }
    
    // Search in legacy contacts field (for backward compatibility)
    if (!match && client.contacts && phoneQuery.length >= 2 && /^\d+$/.test(phoneQuery)) {
      for (const contact of client.contacts) {
        if (contact.telefono && contact.telefono.replace(/\s/g, '').includes(phoneQuery)) {
          match = true;
          matchField = 'Teléfono';
          displayText = contact.telefono;
          matchedContact = contact;
          break;
        }
      }
    }
    
    // UNIVERSAL SEARCH - Search contact names specifically
    if (!match && client.contactos) {
      for (const contact of client.contactos) {
        if (contact.nombre && matchesSearchTerm(contact.nombre, searchTerm)) {
          match = true;
          matchField = 'Contacto';
          displayText = contact.nombre;
          matchedContact = contact;
          break;
        }
        if (contact.email && matchesSearchTerm(contact.email, searchTerm)) {
          match = true;
          matchField = 'Email';
          displayText = contact.email;
          matchedContact = contact;
          break;
        }
      }
    }
    
    // UNIVERSAL SEARCH - Always search text content regardless of search type
    if (!match) {
      const searchableText = buildClientSearchText(client);
      if (matchesSearchTerm(searchableText, searchTerm)) {
        match = true;
        matchField = 'Cliente';
        displayText = getClientDisplayName(client);
      }
    }
    
    if (match) {
      // For phone searches, show the phone number in the subtitle
      const principal = getPrincipalContact(client.contactos);
      let subtitle = '';
      
      if (searchType === 'phone' || matchField === 'Teléfono') {
        // Always show the phone number prominently for phone matches
        subtitle = `📞 ${displayText}`;
        
        // Show the specific contact who owns the phone
        if (matchedContact && matchedContact.nombre) {
          const clientDisplayName = getClientDisplayName(client);
          
          // If the matched contact is different from the main client name, show the contact name
          if (matchedContact.nombre !== clientDisplayName) {
            // Check if this is the principal contact
            const isPrincipal = matchedContact.esPrincipal || (principal && principal.telefono === matchedContact.telefono);
            
            if (isPrincipal) {
              subtitle += ` • ${matchedContact.nombre}`;
            } else {
              subtitle += ` • ${matchedContact.nombre} (contacto adicional)`;
            }
          }
        }
      } else if (matchField === 'Contacto' && matchedContact) {
        // For contact name matches, show the contact prominently
        const clientDisplayName = getClientDisplayName(client);
        
        if (matchedContact.nombre !== clientDisplayName) {
          // Check if this is the principal contact
          const isPrincipal = matchedContact.esPrincipal || (principal && principal.nombre === matchedContact.nombre);
          
          if (isPrincipal) {
            subtitle = `${matchedContact.nombre} (contacto principal)`;
          } else {
            subtitle = `${matchedContact.nombre} (contacto adicional)`;
          }
          
          // Add phone if available
          if (matchedContact.telefono) {
            subtitle += ` • 📞 ${matchedContact.telefono}`;
          }
        } else {
          // This is the same as the main client name
          subtitle = client.tipoCliente === 'empresa' ? 'Empresa' : 'Cliente particular';
        }
      } else if (matchField === 'Email' && matchedContact) {
        // For email matches, show the contact and email
        subtitle = `📧 ${displayText}`;
        
        if (matchedContact.nombre) {
          const clientDisplayName = getClientDisplayName(client);
          
          if (matchedContact.nombre !== clientDisplayName) {
            const isPrincipal = matchedContact.esPrincipal || (principal && principal.email === matchedContact.email);
            
            if (isPrincipal) {
              subtitle += ` • ${matchedContact.nombre}`;
            } else {
              subtitle += ` • ${matchedContact.nombre} (contacto adicional)`;
            }
          }
        }
      } else {
        // For other searches, show contact name or client type
        if (client.nombreEmpresa && principal?.nombre) {
          subtitle = principal.nombre;
        } else {
          subtitle = client.tipoCliente === 'empresa' ? 'Empresa' : 'Cliente particular';
        }
      }
      
      // Get blacklist status for this client
      const clientBlacklistInfo = blacklistStatus[client.id] || { isBlacklisted: false, activeIncidentsCount: 0 };
      
      const result = {
        type: SEARCH_TYPES.CLIENT,
        id: client.id,
        title: getClientDisplayName(client),
        subtitle: subtitle,
        description: (searchType === 'phone' || matchField === 'Teléfono' || matchField === 'Email') ? '' : displayText,
        matchField,
        url: `/clients/${client.id}`,
        data: client,
        isBlacklisted: clientBlacklistInfo.isBlacklisted,
        blacklistIncidentsCount: clientBlacklistInfo.activeIncidentsCount || 0
      };
      
      results.push(result);
    }
  });
  
  return results;
};

/**
 * Searches across orders
 */
const searchOrders = async (searchTerm, searchType) => {
  // Use IndexedDB first to minimize Firestore reads
  try {
    const orders = await getOrdersFromIndexedDB();
    const brands = await fetchAllBrands(); // Get brands for dynamic lookup
    const results = [];
    
    // Get client IDs that match the phone number for cross-referencing
    let matchingClientIds = new Set();
  let clientPhoneMap = new Map(); // Map client ID to phone number
  const phoneQuery = searchTerm.replace(/\s/g, '');
  
  // If search term could be a phone (numeric and 2+ chars), get matching clients
  if (phoneQuery.length >= 2 && /^\d+$/.test(phoneQuery)) {
    const clients = await fetchAllClients();
    
    clients.forEach(client => {
      if (client.contactos) {
        for (const contact of client.contactos) {
          if (contact.telefono && contact.telefono.replace(/\s/g, '').includes(phoneQuery)) {
            matchingClientIds.add(client.id);
            clientPhoneMap.set(client.id, contact.telefono);
            break;
          }
        }
      }
    });
  }
  
  orders.forEach(order => {
    let match = false;
    let matchField = '';
    let displayText = '';
    
    // UNIVERSAL SEARCH - Always check ALL folio fields regardless of search type
    if (order.folio && order.folio.toString().includes(searchTerm)) {
      match = true;
      matchField = 'Folio';
      displayText = `Orden #${order.folio}`;
    }
    
    if (!match && order.folio_cotizacion && order.folio_cotizacion.toString().includes(searchTerm)) {
      match = true;
      matchField = 'Folio de cotización';
      displayText = order.folio_cotizacion;
    }
    
    if (!match && order.folio_orden_compra && order.folio_orden_compra.toString().includes(searchTerm)) {
      match = true;
      matchField = 'Folio orden de compra';
      displayText = order.folio_orden_compra;
    }
    
    if (!match && order.folio_factura_contpaq && order.folio_factura_contpaq.toString().includes(searchTerm)) {
      match = true;
      matchField = 'Folio factura';
      displayText = order.folio_factura_contpaq;
    }
    
    if (!match && order.folio_salida_contpaq && order.folio_salida_contpaq.toString().includes(searchTerm)) {
      match = true;
      matchField = 'Folio salida';
      displayText = order.folio_salida_contpaq;
    }
    
    if (!match && order.folio_compra && order.folio_compra.toString().includes(searchTerm)) {
      match = true;
      matchField = 'Folio de compra';
      displayText = order.folio_compra;
    }
    
    if (!match && order.rma && order.rma.toString().includes(searchTerm)) {
      match = true;
      matchField = 'RMA';
      displayText = order.rma;
    }
    
    // UNIVERSAL SEARCH - Always check phone fields regardless of search type
    if (!match && phoneQuery.length >= 2 && /^\d+$/.test(phoneQuery)) {
      // Search in order's client phone field
      if (order.clientePhone && order.clientePhone.replace(/\s/g, '').includes(phoneQuery)) {
        match = true;
        matchField = 'Teléfono del cliente';
        displayText = order.clientePhone;
      }
      
      // Also search in legacy client phone fields
      if (!match && order.clientPhone && order.clientPhone.replace(/\s/g, '').includes(phoneQuery)) {
        match = true;
        matchField = 'Teléfono del cliente';
        displayText = order.clientPhone;
      }
      
      // Search in client contact phones if available in the order
      if (!match && order.clienteContactPhone && order.clienteContactPhone.replace(/\s/g, '').includes(phoneQuery)) {
        match = true;
        matchField = 'Teléfono del cliente';
        displayText = order.clienteContactPhone;
      }
      
      // Also check if this order belongs to a client with the matching phone number
      if (!match && order.cliente && matchingClientIds.has(order.cliente)) {
        const clientPhone = clientPhoneMap.get(order.cliente);
        match = true;
        matchField = 'Cliente con teléfono';
        displayText = clientPhone || order.clienteName || order.clienteCompanyName || `Cliente ID: ${order.cliente}`;
      }
    }
    
    // UNIVERSAL SEARCH - Always do comprehensive text search regardless of search type
    if (!match) {
      const fieldMatch = findOrderFieldMatch(order, searchTerm, brands);
      
      if (fieldMatch) {
        match = true;
        matchField = fieldMatch.matchField;
        displayText = fieldMatch.matchedText;
      }
    }
    
    if (match) {
      // For phone searches, enhance the subtitle with phone info
      let subtitle = order.clienteName || order.clienteCompanyName || 'Sin cliente';
      if (searchType === 'phone' && displayText && displayText !== subtitle) {
        subtitle += ` • 📞 ${displayText}`;
      }
      
      // Add active/archived indicator
      const statusIndicator = order.isArchived ? ' (Archivada)' : ' (Activa)';
      
      results.push({
        type: SEARCH_TYPES.ORDER,
        id: order.id,
        title: `Orden #${order.folio} - ${order.tipo_de_orden}${statusIndicator}`,
        subtitle: subtitle,
        description: searchType === 'phone' ? '' : displayText,
        matchField,
        url: `/orders/${order.id}`,
        data: order,
        isBookmarked: order.isBookmarked || false
      });
    }
  });
  
  // Sort order results by folio descending (highest first)
  results.sort((a, b) => {
    const aFolio = parseInt(a.data?.folio) || 0;
    const bFolio = parseInt(b.data?.folio) || 0;
    return bFolio - aFolio; // Descending order (highest first)
  });
  
  return results;
  } catch (error) {
    console.error('Error searching orders:', error);
    return [];
  }
};

/**
 * Searches across technicians
 */
const searchTechnicians = async (searchTerm, searchType) => {
  const technicians = await fetchAllTechnicians();
  const results = [];
  
  technicians.forEach(technician => {
    let match = false;
    let matchField = '';
    let displayText = '';
    
    // UNIVERSAL SEARCH - Always check phone numbers regardless of search type
    const phoneQuery = searchTerm.replace(/\s/g, '');
    if (phoneQuery.length >= 2 && /^\d+$/.test(phoneQuery)) {
      if (technician.telefono && technician.telefono.replace(/\s/g, '').includes(phoneQuery)) {
        match = true;
        matchField = 'Teléfono';
        displayText = technician.telefono;
      }
    }
    
    // UNIVERSAL SEARCH - Always do text search regardless of search type
    if (!match) {
      const searchableText = buildTechnicianSearchText(technician);
      if (matchesSearchTerm(searchableText, searchTerm)) {
        match = true;
        matchField = 'Técnico';
        displayText = `${technician.nombre || ''} ${technician.apellido || ''}`.trim();
      }
    }
    
    if (match) {
      // For phone searches, show the phone number in the subtitle
      let subtitle = '';
      if (searchType === 'phone') {
        subtitle = `📞 ${displayText}`;
        if (technician.especialidad) {
          subtitle += ` • ${technician.especialidad}`;
        }
      } else {
        subtitle = technician.especialidad || 'Técnico';
      }
      
      results.push({
        type: SEARCH_TYPES.TECHNICIAN,
        id: technician.id,
        title: `${technician.nombre || ''} ${technician.apellido || ''}`.trim(),
        subtitle: subtitle,
        description: searchType === 'phone' ? '' : displayText,
        matchField,
        url: `/technicians/${technician.id}`,
        data: technician
      });
    }
  });
  
  return results;
};

/**
 * Searches across equipment catalog
 */
const searchEquipment = async (searchTerm, searchType) => {
  const equipment = await fetchAllEquipment();
  const brands = await fetchAllBrands();
  const results = [];
  
  equipment.forEach(equip => {
    let match = false;
    let matchField = '';
    let displayText = '';
    
    // UNIVERSAL SEARCH - Always search all equipment fields regardless of search type
    const searchableText = buildEquipmentSearchText(equip, brands);
    if (matchesSearchTerm(searchableText, searchTerm)) {
      match = true;
      
      // Determine which field matched
      if (equip.tipo && matchesSearchTerm(equip.tipo, searchTerm)) {
        matchField = 'Tipo';
        displayText = equip.tipo;
      } else if (equip.marcaId) {
        const brand = brands.find(b => b.id === equip.marcaId);
        const brandName = brand?.nombre || 'Sin marca';
        if (matchesSearchTerm(brandName, searchTerm)) {
          matchField = 'Marca';
          displayText = brandName;
        }
      }
      
      if (!matchField) {
        if (equip.modelo && matchesSearchTerm(equip.modelo, searchTerm)) {
          matchField = 'Modelo';
          displayText = equip.modelo;
        } else if (equip.numeroParte && matchesSearchTerm(equip.numeroParte, searchTerm)) {
          matchField = 'Número de Parte';
          displayText = equip.numeroParte;
        } else if (equip.partesAlternativas && Array.isArray(equip.partesAlternativas)) {
          // Check alternate part numbers
          const matchedAltPart = equip.partesAlternativas.find(altPart => 
            altPart && altPart.trim() && matchesSearchTerm(altPart, searchTerm)
          );
          if (matchedAltPart) {
            matchField = 'Número de Parte Alternativo';
            displayText = matchedAltPart;
          }
        }
        
        if (!matchField) {
          if (equip.descripcion && matchesSearchTerm(equip.descripcion, searchTerm)) {
            matchField = 'Descripción';
            displayText = equip.descripcion;
          } else {
            matchField = 'Equipo';
            displayText = equip.modelo || equip.tipo || 'Equipo';
          }
        }
      }
    }
    
    if (match) {
      const brand = brands.find(b => b.id === equip.marcaId);
      const brandName = brand?.nombre || 'Sin marca';
      
      results.push({
        type: SEARCH_TYPES.EQUIPMENT,
        id: equip.id,
        title: `${equip.tipo || ''} ${brandName || ''} ${equip.modelo || ''}`.trim(),
        subtitle: equip.numeroParte || 'Equipo de catálogo',
        description: displayText,
        matchField,
        url: `/equipment/${equip.id}`,
        data: equip
      });
    }
  });
  
  return results;
};

/**
 * Gets orders from IndexedDB for better performance
 */
const getOrdersFromIndexedDB = async () => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      
      request.onsuccess = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains('orders')) {
          resolve([]);
          return;
        }
        
        const transaction = db.transaction(['orders'], 'readonly');
        const objectStore = transaction.objectStore('orders');
        const getAllRequest = objectStore.getAll();
        
        getAllRequest.onsuccess = () => {
          resolve(getAllRequest.result || []);
        };
        
        getAllRequest.onerror = () => {
          reject(getAllRequest.error);
        };
      };
    });
  } catch (error) {
    console.warn('Error accessing IndexedDB for global search, falling back to Firestore:', error);
    // Fallback to Firestore but try to minimize the impact
    try {
      const q = query(collection(db, 'orders'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (firestoreError) {
      console.error('Error accessing Firestore as fallback:', firestoreError);
      return [];
    }
  }
};

/**
 * Finds orders that use specific equipment from catalog
 */
const findOrdersUsingEquipment = async (equipmentId, allOrders = null) => {
  const orders = allOrders || await getOrdersFromIndexedDB();
  return orders.filter(order => {
    if (!order.equipos) return false;
    return order.equipos.some(equip => equip.catalogId === equipmentId);
  });
};

/**
 * Finds orders from specific client
 */
const findOrdersFromClient = async (clientId, allOrders = null) => {
  const orders = allOrders || await getOrdersFromIndexedDB();
  return orders.filter(order => order.cliente === clientId);
};

/**
 * Finds orders assigned to specific technician
 */
const findOrdersFromTechnician = async (technicianId, allOrders = null) => {
  const orders = allOrders || await getOrdersFromIndexedDB();
  return orders.filter(order => {
    if (!order.tecnicos) return false;
    return order.tecnicos.includes(technicianId);
  });
};

/**
 * Creates cross-reference results for orders
 */
const createCrossReferenceResults = (orders, sourceType, sourceData, searchTerm) => {
  // Sort orders by folio descending before creating results
  const sortedOrders = orders.sort((a, b) => {
    const aFolio = parseInt(a.folio) || 0;
    const bFolio = parseInt(b.folio) || 0;
    return bFolio - aFolio; // Descending order (highest first)
  });
  
  return sortedOrders.slice(0, 3).map(order => ({ // Limit to 3 cross-references to avoid overwhelming results
    type: SEARCH_TYPES.ORDER,
    id: order.id,
    title: `Orden #${order.folio} - ${order.tipo_de_orden}${order.isArchived ? ' (Archivada)' : ''}`,
    subtitle: `Relacionada con ${sourceType}: ${sourceData.title || sourceData.name}`,
    description: order.clienteName || order.clienteCompanyName || 'Sin cliente',
    matchField: `${sourceType} relacionado`,
    url: `/orders/${order.id}`,
    data: order,
    isBookmarked: order.isBookmarked || false,
    isCrossReference: true
  }));
};

/**
 * Main global search function
 */
export const globalSearch = async (searchTerm, maxResults = 15) => {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return [];
  }
  
  const cleanSearchTerm = searchTerm.trim();
  const searchType = detectSearchType(cleanSearchTerm);
  
  try {
    // Parallel search across all data types
    const [orderResults, clientResults, technicianResults, equipmentResults] = await Promise.all([
      searchOrders(cleanSearchTerm, searchType),
      searchClients(cleanSearchTerm, searchType),
      searchTechnicians(cleanSearchTerm, searchType),
      searchEquipment(cleanSearchTerm, searchType)
    ]);
    
    // Combine direct results
    let allResults = [...orderResults, ...clientResults, ...technicianResults, ...equipmentResults];
    
    // Add cross-reference results for comprehensive search
    const crossReferenceResults = [];
    
    // Get orders once for all cross-reference searches (use fresh Firestore data)
    let allOrdersCache = null;
    const needsCrossReferences = clientResults.length > 0 || equipmentResults.length > 0 || technicianResults.length > 0;
    
    if (needsCrossReferences) {
      // Use IndexedDB for cross-references to minimize Firestore reads
      allOrdersCache = await getOrdersFromIndexedDB();
    }
    
    // If we found clients, add their orders
    if (clientResults.length > 0) {
      for (const client of clientResults.slice(0, 2)) { // Limit to first 2 clients to avoid too many results
        const clientOrders = await findOrdersFromClient(client.id, allOrdersCache);
        if (clientOrders.length > 0) {
          crossReferenceResults.push(...createCrossReferenceResults(clientOrders, 'Cliente', client, searchTerm));
        }
      }
    }
    
    // If we found equipment, add orders using that equipment
    if (equipmentResults.length > 0) {
      for (const equipment of equipmentResults.slice(0, 2)) { // Limit to first 2 equipment items
        const equipmentOrders = await findOrdersUsingEquipment(equipment.id, allOrdersCache);
        if (equipmentOrders.length > 0) {
          crossReferenceResults.push(...createCrossReferenceResults(equipmentOrders, 'Equipo', equipment, searchTerm));
        }
      }
    }
    
    // If we found technicians, add orders assigned to them
    if (technicianResults.length > 0) {
      for (const technician of technicianResults.slice(0, 2)) { // Limit to first 2 technicians
        const technicianOrders = await findOrdersFromTechnician(technician.id, allOrdersCache);
        if (technicianOrders.length > 0) {
          crossReferenceResults.push(...createCrossReferenceResults(technicianOrders, 'Técnico', technician, searchTerm));
        }
      }
    }
    
    // Add cross-reference results to main results
    allResults.push(...crossReferenceResults);
    
    // Remove duplicates (same order might appear from different cross-references)
    const uniqueResults = [];
    const seenUrls = new Set();
    
    for (const result of allResults) {
      const key = `${result.type}-${result.id}`;
      if (!seenUrls.has(key)) {
        seenUrls.add(key);
        uniqueResults.push(result);
      }
    }
    
    // Sort by relevance - prioritize direct matches, then exact title matches
    uniqueResults.sort((a, b) => {
      const cleanLowerTerm = cleanSearchTerm.toLowerCase();
      
      // Direct matches have higher priority than cross-references
      const aCrossRef = a.isCrossReference ? 1 : 0;
      const bCrossRef = b.isCrossReference ? 1 : 0;
      if (aCrossRef !== bCrossRef) {
        return aCrossRef - bCrossRef;
      }
      
      // For numeric searches, prioritize exact folio matches
      if (/^\d+$/.test(cleanSearchTerm)) {
        const aExactFolio = (a.matchField === 'Folio' && a.title.includes(`#${cleanSearchTerm}`)) ? 0 : 1;
        const bExactFolio = (b.matchField === 'Folio' && b.title.includes(`#${cleanSearchTerm}`)) ? 0 : 1;
        if (aExactFolio !== bExactFolio) {
          return aExactFolio - bExactFolio;
        }
      }
      
      // Then sort by exact matches in title
      const aExact = a.title.toLowerCase().includes(cleanLowerTerm) ? 0 : 1;
      const bExact = b.title.toLowerCase().includes(cleanLowerTerm) ? 0 : 1;
      if (aExact !== bExact) {
        return aExact - bExact;
      }
      
      // For orders specifically, sort by folio descending (highest first)
      if (a.type === SEARCH_TYPES.ORDER && b.type === SEARCH_TYPES.ORDER) {
        const aFolio = parseInt(a.data?.folio) || 0;
        const bFolio = parseInt(b.data?.folio) || 0;
        if (aFolio !== bFolio) {
          return bFolio - aFolio; // Descending order (highest first)
        }
      }
      
      // Finally, maintain some logical ordering: orders, clients, equipment, technicians
      const typeOrder = { order: 0, client: 1, equipment: 2, technician: 3 };
      const aTypeOrder = typeOrder[a.type] || 4;
      const bTypeOrder = typeOrder[b.type] || 4;
      return aTypeOrder - bTypeOrder;
    });
    
    return uniqueResults.slice(0, maxResults);
  } catch (error) {
    console.error('Error performing global search:', error);
    return [];
  }
};

export default globalSearch;